import React from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImgIcon, Loader2 } from "lucide-react";

/**
 * Image upload component — POSTs to /api/admin/upload and stores public_url.
 * Drop-in replacement for an <input type="url" /> field.
 *
 * Props:
 *  - value: current URL (string)
 *  - onChange(url): callback
 *  - testId: optional test id base
 *  - label: optional label text
 */
export default function ImageUpload({ value, onChange, testId = "image-upload", label = "Image" }) {
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image trop lourde (max 8 Mo)");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("cef_admin_token");
      const r = await fetch(`${API_BASE}/admin/upload`, {
        method: "POST",
        body: form,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      // The backend returns relative public_url like /api/files/...
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      const fullUrl = data.public_url.startsWith("http") ? data.public_url : `${baseUrl}${data.public_url}`;
      onChange(fullUrl);
      toast.success("Image uploadée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload échoué");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid gap-2" data-testid={testId}>
      {label && <label className="text-xs font-bold uppercase tracking-tight">{label}</label>}
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="Aperçu" className="max-h-40 border-2 border-black rounded-md bg-white" data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-1 right-1 w-7 h-7 inline-flex items-center justify-center bg-cef-pink text-white border-2 border-black rounded-md hover:bg-black"
            aria-label="Retirer l'image"
            data-testid={`${testId}-clear`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-black/40 rounded-md p-4 bg-cef-paper text-center text-sm text-neutral-500">
          <ImgIcon className="w-6 h-6 mx-auto mb-1" /> Aucune image
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testId}-file-input`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="neo-btn bg-cef-blue text-white text-sm py-2 disabled:opacity-50"
          data-testid={`${testId}-trigger`}
        >
          {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Upload…</> : <><Upload className="w-4 h-4" /> {value ? "Remplacer" : "Uploader une image"}</>}
        </button>
        <input
          className="neo-input flex-1 text-sm"
          placeholder="…ou colle une URL"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-url`}
        />
      </div>
    </div>
  );
}
